import type { ReactNode } from "react";
import { Card, Chip } from "@/components/ds";
import { isValidHex } from "@/lib/brandContrast";
import type { Organization } from "@/types/organizations";

interface OrganizationBrandingCardProps {
  organization: Pick<
    Organization,
    "primaryColor" | "secondaryColor" | "accentColor" | "employeeAppBrandingEnabled" | "usesAthlynApp"
  >;
  /** Footer action, e.g. the Overview's "Edit branding" link. */
  action?: ReactNode;
}

interface ColourRowProps {
  label: string;
  value: string | null | undefined;
  testId: string;
  emptyText: string;
}

function ColourRow({ label, value, testId, emptyText }: ColourRowProps) {
  const hex = isValidHex(value) ? value.toUpperCase() : null;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] text-ds-ink3">{label}</span>
      <span data-testid={testId} className="flex items-center gap-2 text-[13.5px] font-medium text-ds-ink">
        {hex ? (
          <>
            <span
              aria-hidden="true"
              className="inline-block h-4 w-4 rounded border border-ds-line2"
              // The company's own colour (data), not a design token.
              style={{ backgroundColor: hex }}
            />
            <span className="font-ds-mono">{hex}</span>
          </>
        ) : (
          <span className="text-ds-ink3">{emptyText}</span>
        )}
      </span>
    </div>
  );
}

/** Organisation detail (Overview): the app colours and the "Brand the employee app" switch. */
export function OrganizationBrandingCard({ organization, action }: OrganizationBrandingCardProps) {
  // Branding follows the app package: only companies on the Athlyn app take it.
  const usesAthlynApp = organization.usesAthlynApp === true;
  const on = (organization.employeeAppBrandingEnabled ?? false) && usesAthlynApp;
  return (
    <Card className="px-4 py-3.5" data-testid="org-branding-card">
      <h2 className="mb-2.5 text-[14px] font-semibold text-ds-ink">Branding</h2>
      <div className="space-y-2">
        <ColourRow label="Main colour" value={organization.primaryColor} testId="org-branding-main" emptyText="Not set" />
        <ColourRow
          label="Highlight colour"
          value={organization.secondaryColor}
          testId="org-branding-highlight"
          emptyText="Not set"
        />
        <ColourRow
          label="Accent colour"
          value={organization.accentColor}
          testId="org-branding-accent"
          emptyText="Derived from highlight"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] text-ds-ink3">Brand the employee app</span>
          <span data-testid="org-branding-enabled" className="flex items-center gap-2">
            <Chip tone={on ? "ok" : "neutral"}>{on ? "On" : "Off"}</Chip>
            {!usesAthlynApp && <span className="text-xs text-ds-ink3">own app</span>}
          </span>
        </div>
      </div>
      {action && <div className="mt-3">{action}</div>}
    </Card>
  );
}

export default OrganizationBrandingCard;
